import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import Linkify from 'linkify-react'
import Image from 'next/image'
import React, { memo } from 'react'

import AddressSpanWithCopyButton from '@/components/base/AddressSpanWithCopyButton'
import BaseDialog from '@/components/base/BaseDialog'
import useTrans from '@/hooks/useTrans'
import { APITokenInfo } from '@/utils/apis/helper'
import { getAddressUrl } from '@/utils/helpers'

type TokenDetailDialogProps = {
  token: APITokenInfo,
  handleClose: () => void
}

const TokenDetailDialog = (props: TokenDetailDialogProps) => {
  const { handleClose, token } = props
  const trans = useTrans()

  return (
    <BaseDialog
      data-testid="token-detail-dialog"
      title={trans.core_tokens_list.token_detail.title}
      isOpened={true}
      handleClose={handleClose}
      body={
        <Box className="w-[488px] px-2 max-w-full">
          <Stack alignItems="center" className="my-5">
            <Box className="w-[128px] sm:w-[180px] h-[128px] sm:h-[180px] mb-4">
              <Image src={token.logo_url} alt="reUSD" width={180} height={180} className="max-w-full rounded-full" />
            </Box>
            <Stack direction="row" alignItems="center">
              <Typography className="mr-1" fontWeight="700" data-testid="token-symbol">{token.token_symbol}</Typography>
              <Typography sx={{ color: '#D0D1D8', fontSize: '0.875rem' }} data-testid="token-name">- {token.token_name}</Typography>
            </Stack>
            <AddressSpanWithCopyButton address={token.coin_address} />
          </Stack>

          <Box className="mb-4">
            <Typography className="mb-2 text-sm text-gray-300" data-testid="description-title">
              {trans.core_tokens_list.table_headers.about}
            </Typography>
            <Typography
              fontSize="0.875rem"
              overflow="wrap"
              data-testid="description"
              className="text-gray-100 border border-semiblack-400 rounded-md px-4 py-3 w-full bg-[#1F2A3A]"
            >
              <Linkify>{token.description}</Linkify>
            </Typography>
          </Box>

          <Box className="mb-4">
            <Typography className="mb-2 text-sm text-gray-300" data-testid="link-title">
              {trans.core_tokens_list.token_detail.link}
            </Typography>
            <Stack
              direction="row"
              className="text-gray-100 border border-semiblack-400 rounded-md px-4 py-3 w-full bg-[#1F2A3A]"
            >
              <Link
                href={getAddressUrl(token.coin_address)}
                target="_blank"
                className="mr-10"
                data-testid="explorer-url"
              >
                <Typography className="text-sm">Explorer&#8599;</Typography>
              </Link>
              {token.website && <Link href={token.website} target="_blank">
                <Typography className="text-sm">{trans.core_tokens_list.token_detail.website}&#8599;</Typography>
              </Link>
              }
            </Stack>
          </Box>
        </Box>
      }
      button={
        <Button
          data-testid="close-btn"
          className="normal-case rounded-lg py-[14px] border-semiblack-600"
          variant="outlined"
          fullWidth
          size="large"
          onClick={handleClose}
        >
          {trans.core_tokens_list.token_detail.close}
        </Button>
      }
    />
  )
}

export default memo(TokenDetailDialog)
